/**
 * Oddit global interactions — static preview bundle.
 * Adds html.oddit-js, stagger indices, reveal IO, section activation, parallax.
 * Reveals use threshold 0 so “peek-a-boo” carousel slides (only a sliver visible)
 * still get is-visible on load; a post-layout flush matches the same root bounds.
 *
 * Theme editor: re-applies visibility on shopify:section:load / editor load so
 * re-rendered sections are not stuck at opacity 0 (missing oddit-section-active / is-visible).
 */
(function () {
  'use strict';

  document.documentElement.classList.add('oddit-js');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var staticPreview = document.documentElement.classList.contains(
    'oddit-static-preview',
  );

  var REVEAL_LOOKAHEAD = 0.25;
  var FAST_SCROLL_PX_PER_MS = 1.5;
  var FAST_SCROLL_RESET_MS = 250;

  var sectionShellObserver = null;
  var revealObserver = null;
  var parallaxInitialized = false;
  var requestParallaxUpdate = null;
  var revealScrollInitialized = false;
  var lastScrollY = 0;
  var lastScrollTime = 0;
  var scrollTicking = false;
  var fastScrollTimer = null;

  function isThemeEditor() {
    return (
      typeof window.Shopify !== 'undefined' &&
      window.Shopify.designMode === true
    );
  }

  function getStaggerWrap(container) {
    var style = window.getComputedStyle(container);
    if (style.display.indexOf('grid') === -1) {
      return 0;
    }
    var tracks = style.gridTemplateColumns;
    if (!tracks || tracks === 'none' || tracks.indexOf('(') !== -1) {
      return 0;
    }
    var columns = tracks.split(/\s+/).filter(Boolean).length;
    return columns > 1 ? columns : 0;
  }

  function initStagger(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-oddit-stagger]').forEach(function (container) {
      var wrap = getStaggerWrap(container);
      var idx = 0;
      Array.prototype.forEach.call(container.children, function (child) {
        if (child.classList && child.classList.contains('oddit-reveal')) {
          var index = wrap > 0 ? idx % wrap : idx;
          child.style.setProperty('--oddit-stagger-index', String(index));
          idx += 1;
        }
      });
    });
  }

  function ensureSectionShellObserver() {
    if (staticPreview || reducedMotion.matches) {
      return null;
    }
    if (!sectionShellObserver) {
      sectionShellObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('oddit-section-active');
            }
          });
        },
        { rootMargin: '0px 0px -5% 0px', threshold: 0.01 },
      );
    }
    return sectionShellObserver;
  }

  function observeShopifySection(el) {
    if (staticPreview || reducedMotion.matches) {
      el.classList.add('oddit-section-active');
      return;
    }
    var io = ensureSectionShellObserver();
    if (io) {
      io.observe(el);
    }
  }

  /**
   * Axis-aligned intersection against the same effective root as the reveal
   * IntersectionObserver (viewport + rootMargin). Used to flush reveals after
   * layout so peek-a-boo slides are not stuck hidden for a frame.
   */
  function getRevealRootBounds() {
    var w = window.innerWidth || 0;
    var h = window.innerHeight || 0;
    return {
      top: 0,
      left: 0,
      right: w,
      bottom: h + h * REVEAL_LOOKAHEAD,
    };
  }

  function rectIntersectsRevealRoot(rect, root) {
    var bounds = root || getRevealRootBounds();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.left < bounds.right &&
      rect.right > bounds.left &&
      rect.top < bounds.bottom &&
      rect.bottom > bounds.top
    );
  }

  /**
   * Mark any already-intersecting reveals visible (and unobserve). Covers
   * carousels that show part of the next slide: a small visible fraction can be
   * below the old 5% IO threshold; threshold 0 fixes IO, this catches late layout.
   */
  function flushInitiallyVisibleReveals(scope) {
    if (reducedMotion.matches || staticPreview || isThemeEditor()) {
      return;
    }
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = root.querySelectorAll('.oddit-reveal:not(.is-visible)');
    if (!nodes.length) {
      return;
    }
    var bounds = getRevealRootBounds();
    var due = [];
    for (var i = 0; i < nodes.length; i++) {
      if (rectIntersectsRevealRoot(nodes[i].getBoundingClientRect(), bounds)) {
        due.push(nodes[i]);
      }
    }
    for (var j = 0; j < due.length; j++) {
      due[j].classList.add('is-visible');
      if (revealObserver) {
        revealObserver.unobserve(due[j]);
      }
    }
  }

  function setFastScroll(on) {
    document.documentElement.classList.toggle('oddit-scroll-fast', on);
  }

  function onRevealScroll() {
    if (scrollTicking) {
      return;
    }
    scrollTicking = true;
    requestAnimationFrame(function (now) {
      scrollTicking = false;
      var y = window.pageYOffset || 0;
      var elapsed = now - lastScrollTime;
      var velocity = elapsed > 0 ? Math.abs(y - lastScrollY) / elapsed : 0;
      lastScrollY = y;
      lastScrollTime = now;

      if (velocity > FAST_SCROLL_PX_PER_MS) {
        setFastScroll(true);
        clearTimeout(fastScrollTimer);
        fastScrollTimer = setTimeout(function () {
          setFastScroll(false);
        }, FAST_SCROLL_RESET_MS);
      }

      flushInitiallyVisibleReveals(document);
    });
  }

  function initRevealScrollFlush() {
    if (revealScrollInitialized || staticPreview || reducedMotion.matches) {
      return;
    }
    revealScrollInitialized = true;
    lastScrollY = window.pageYOffset || 0;
    lastScrollTime =
      typeof performance !== 'undefined' && performance.now
        ? performance.now()
        : 0;
    window.addEventListener('scroll', onRevealScroll, { passive: true });
    window.addEventListener('resize', onRevealScroll, { passive: true });
  }

  function ensureRevealObserver() {
    if (reducedMotion.matches) {
      return null;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px ' + REVEAL_LOOKAHEAD * 100 + '% 0px',
          threshold: 0,
        },
      );
    }
    return revealObserver;
  }

  function observeReveal(el) {
    if (reducedMotion.matches) {
      el.classList.add('is-visible');
      return;
    }
    var io = ensureRevealObserver();
    if (io) {
      io.observe(el);
    }
  }

  function initShopifySections() {
    var sections = document.querySelectorAll('.shopify-section');
    if (!sections.length) {
      return;
    }

    if (staticPreview || reducedMotion.matches) {
      sections.forEach(function (el) {
        el.classList.add('oddit-section-active');
      });
      return;
    }

    var io = ensureSectionShellObserver();
    if (!io) {
      return;
    }
    sections.forEach(function (el) {
      io.observe(el);
    });
  }

  function initReveals() {
    var nodes = document.querySelectorAll('.oddit-reveal');
    if (!nodes.length) {
      return;
    }

    if (reducedMotion.matches) {
      nodes.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    nodes.forEach(function (el) {
      observeReveal(el);
    });
  }

  function initParallax() {
    if (reducedMotion.matches) {
      return;
    }

    var ticking = false;

    function update() {
      ticking = false;
      var els = document.querySelectorAll('.oddit-parallax');
      var vh = window.innerHeight || 1;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var rect = el.getBoundingClientRect();
        var raw = el.getAttribute('data-oddit-parallax-speed');
        var speed = raw != null ? parseFloat(raw) : 0.15;
        if (Number.isNaN(speed)) {
          speed = 0.15;
        }
        var centerOffset = rect.top + rect.height / 2 - vh / 2;
        var relative = centerOffset / vh;
        var ty = relative * 100 * speed;
        el.style.transform = 'translate3d(0, ' + ty + 'px, 0)';
      }
    }

    function onFrame() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    requestParallaxUpdate = onFrame;

    if (!parallaxInitialized) {
      parallaxInitialized = true;
      window.addEventListener('scroll', onFrame, { passive: true });
      window.addEventListener('resize', onFrame, { passive: true });
    }
    onFrame();
  }

  /**
   * Theme editor: new DOM has no oddit-section-active / is-visible; apply immediately.
   * Storefront section AJAX: wire IntersectionObserver for the replaced section subtree.
   */
  function attachInteractionsToSectionRoot(sectionRoot) {
    if (!sectionRoot || !sectionRoot.querySelectorAll) {
      return;
    }

    if (staticPreview || reducedMotion.matches) {
      initStagger(sectionRoot);
      if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
        sectionRoot.classList.add('oddit-section-active');
      }
      sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (isThemeEditor()) {
      initStagger(sectionRoot);
      if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
        sectionRoot.classList.add('oddit-section-active');
      }
      sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    initStagger(sectionRoot);
    if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
      observeShopifySection(sectionRoot);
    }
    sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
      observeReveal(el);
    });
    requestAnimationFrame(function () {
      flushInitiallyVisibleReveals(sectionRoot);
    });
  }

  /**
   * Public hook for sections that replace their own DOM (collection filtering,
   * pagination). Freshly-injected .oddit-reveal nodes are observed by nothing and
   * would sit at opacity 0 forever; pass the swapped subtree to re-arm them.
   */
  window.OdditInteractions = window.OdditInteractions || {};
  window.OdditInteractions.attach = attachInteractionsToSectionRoot;

  function refreshAllThemeEditorInteractions() {
    if (!isThemeEditor()) {
      return;
    }
    initStagger();
    document.querySelectorAll('.shopify-section').forEach(function (el) {
      el.classList.add('oddit-section-active');
    });
    document.querySelectorAll('.oddit-reveal').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  function init() {
    initStagger();

    if (isThemeEditor()) {
      refreshAllThemeEditorInteractions();
      initParallax();
      return;
    }

    initShopifySections();
    initReveals();
    initRevealScrollFlush();
    requestAnimationFrame(function () {
      flushInitiallyVisibleReveals(document);
    });
    initParallax();
  }

  function onShopifySectionLoad(e) {
    if (e && e.target) {
      attachInteractionsToSectionRoot(e.target);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    } else {
      initParallax();
    }
  }

  document.addEventListener('shopify:section:load', onShopifySectionLoad);
  document.addEventListener('shopify:section:select', function (e) {
    if (e && e.target) {
      attachInteractionsToSectionRoot(e.target);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });
  document.addEventListener('shopify:block:select', function (e) {
    var section =
      e &&
      e.target &&
      e.target.closest &&
      e.target.closest('.shopify-section');
    if (section) {
      attachInteractionsToSectionRoot(section);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });
  document.addEventListener('shopify:editor:load', function () {
    refreshAllThemeEditorInteractions();
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });

  reducedMotion.addEventListener('change', function (e) {
    if (e.matches) {
      document.querySelectorAll('.shopify-section').forEach(function (el) {
        el.classList.add('oddit-section-active');
      });
      document.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      if (sectionShellObserver) {
        sectionShellObserver.disconnect();
        sectionShellObserver = null;
      }
      if (revealObserver) {
        revealObserver.disconnect();
        revealObserver = null;
      }
      clearTimeout(fastScrollTimer);
      setFastScroll(false);
    } else {
      if (staticPreview) {
        return;
      }
      if (isThemeEditor()) {
        refreshAllThemeEditorInteractions();
      } else {
        initShopifySections();
        document.querySelectorAll('.oddit-reveal:not(.is-visible)').forEach(function (el) {
          observeReveal(el);
        });
        initRevealScrollFlush();
      }
      initParallax();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
